import React, { Component } from "react";
import { Link, withRouter } from "react-router-dom";
import logo from "../../assets/images/logo-sm-light.png";
import { getBranchList, getActiveBranches, getMyCatalogCounts } from "../../Endpoint/Admin";
import {
  hasModuleAccess,
  isSuperAdmin,
  isBranchScopedAdmin,
  canAccessAllBranches,
  getUserBranchId,
  getUserBranchName,
  getSelectedBranchId,
  setSelectedBranchId,
  getCurrentUser,
} from "../../services/PermissionService";

/**
 * Operations console sidebar.
 *
 * Rebuilt to the redesign handoff: 248px dark-brown rail, branch picker below
 * the wordmark, section-grouped nav, solid white pill for the active item, and
 * the signed-in user pinned to the bottom.
 *
 * Nav content is unchanged from the previous sidebar — same modules, same
 * permission gates, same routes. Only the presentation is new, so nothing a
 * user could reach before disappears here.
 */

// One list drives the whole rail. `mod` is the permission gate; `superOnly`
// marks entries only a super admin sees.
const NAV = [
  { to: "/dashboard", icon: "grid_view", label: "Dashboard", mod: "DASHBOARD" },

  { section: "Operations" },
  { to: "/Food", icon: "restaurant", label: "Food", mod: "FOOD" },
  { to: "/Room", icon: "bed", label: "Rooms", mod: "ROOM" },
  { to: "/rooms/units", icon: "door_front", label: "Room stock", mod: "ROOM" },
  { to: "/rooms/availability", icon: "calendar_month", label: "Availability", mod: "ROOM" },
  { to: "/Table", icon: "table_restaurant", label: "Table Booking", mod: "TABLE" },
  { to: "/outlets", icon: "storefront", label: "Restaurants & bars", mod: "TABLE" },
  { to: "/transport/requests", icon: "local_taxi", label: "Transport", mod: "TRANSPORTATION" },
  { to: "/Taxi", icon: "directions_car", label: "Taxi", mod: "TAXI" },

  { section: "Customers" },
  { to: "/User", icon: "group", label: "Users", mod: "USERS" },
  { to: "/loyalty/accounts", icon: "redeem", label: "Loyalty", mod: "LOYALTY" },
  { to: "/ChatList", icon: "forum", label: "Chat", mod: "CHAT" },
  { to: "/Notification", icon: "notifications", label: "Notifications", mod: "NOTIFICATION" },

  { section: "Marketing" },
  { to: "/coupon", icon: "sell", label: "Coupons", mod: "COUPON" },
  { to: "/banner", icon: "image", label: "Banners", mod: "BANNER" },
  { to: "/Attraction", icon: "attractions", label: "Attractions", mod: "ATTRACTION" },
  { to: "/crosssell", icon: "auto_awesome", label: "Cross-sell", mod: "CROSSSELL" },

  { section: "Finance & insights" },
  { to: "/payments/transactions", icon: "payments", label: "Transactions", mod: "PAYMENTS" },
  { to: "/analytics", icon: "monitoring", label: "Analytics", mod: "ANALYTICS" },
  { to: "/activity-logs", icon: "history", label: "Activity Logs", superOnly: true },

  { section: "Administration" },
  { to: "/branches", icon: "apartment", label: "Branches", superOnly: true },
  { to: "/master-catalog", icon: "inventory_2", label: "Master Catalog", superOnly: true },
  { to: "/team", icon: "badge", label: "Team", mod: "TEAM" },
  { to: "/FoodSettings", icon: "settings", label: "Settings", mod: "SETTINGS" },
];

class ConsoleSidebar extends Component {
  constructor(props) {
    super(props);
    this.state = {
      branches: [],
      branchName: isBranchScopedAdmin() ? getUserBranchName() : null,
      pickerOpen: false,
      // Which catalog categories the branch in effect actually carries, keyed
      // by the admin route of each category. null until loaded, and null for a
      // global admin viewing all branches (nothing to narrow).
      carriedByRoute: null,
    };
  }

  /**
   * Hide operational nav entries for things the branch in effect does not sell -
   * the airport branch carries no rooms, so "Rooms" is noise there.
   *
   * Deliberately ONLY for branch-scoped admins. A global admin is the person who
   * grants a branch a category in the first place, and they do that from
   * Branches -> Catalog for existing items; but creating a brand-new room for a
   * room-less branch still needs the Rooms page. Hiding it from them would be a
   * trap with no way out, so they keep the full rail and simply see an empty
   * page - which already says "No rooms yet".
   */
  loadCarried() {
    if (!isBranchScopedAdmin()) return;
    // No branchId passed: the server resolves the caller's own branch, which is
    // why a branch admin may call this at all. getMasterCatalog is super-admin
    // only, so the first attempt at this silently failed and nothing was hidden.
    getMyCatalogCounts().then((res) => {
      const groups = (res && res.data && res.data.groups) || null;
      if (!groups) return;
      const carriedByRoute = {};
      groups.forEach((g) => {
        if (g.adminRoute) carriedByRoute[g.adminRoute] = g.carried;
      });
      this.setState({ carriedByRoute });
    });
  }

  componentDidMount() {
    this.loadCarried();
    if (canAccessAllBranches()) {
      getBranchList().then((res) => {
        if (res && res.success && Array.isArray(res.data)) this.setState({ branches: res.data });
      });
      return;
    }
    // A scoped admin cannot switch, but still needs their branch named. The
    // login payload carries it; fall back to a lookup for older sessions.
    if (this.state.branchName) return;
    const id = getUserBranchId();
    if (!id) return;
    getActiveBranches().then((res) => {
      const list = res && res.data;
      if (!Array.isArray(list)) return;
      const b = list.find((x) => String(x._id) === String(id));
      if (b) this.setState({ branchName: b.name || b.code });
    });
  }

  currentBranchLabel() {
    if (isBranchScopedAdmin()) return this.state.branchName || "—";
    const sel = getSelectedBranchId();
    if (!sel) return "All branches";
    const b = this.state.branches.find((x) => String(x._id) === String(sel));
    return b ? b.name : "All branches";
  }

  pick = (branchId) => {
    setSelectedBranchId(branchId);
    // Every page fetches its own branch-scoped data, so a reload is the
    // honest way to make the whole console agree on the new scope.
    window.location.reload();
  };

  visible(item) {
    if (item.section) return true;
    if (item.superOnly) return isSuperAdmin();
    if (item.mod && !hasModuleAccess(item.mod)) return false;

    // Then: does this branch actually carry the thing? Only applied where we
    // know the answer, so the rail never hides an entry because a request has
    // not come back yet.
    const carried = this.state.carriedByRoute;
    if (carried && Object.prototype.hasOwnProperty.call(carried, item.to)) {
      return carried[item.to] > 0;
    }
    return true;
  }

  render() {
    const { pickerOpen, branches } = this.state;
    const user = getCurrentUser() || {};
    const initials = `${(user.firstName || "?")[0] || ""}${(user.lastName || "")[0] || ""}`.toUpperCase();
    const path = this.props.location ? this.props.location.pathname : "";
    const switchable = canAccessAllBranches();

    // Drop section headings whose every item is hidden, so a limited account
    // never sees a heading standing over nothing.
    const items = [];
    NAV.forEach((item) => {
      if (item.section) {
        items.push(item);
      } else if (this.visible(item)) {
        items.push(item);
      }
    });
    const pruned = items.filter((item, i) => {
      if (!item.section) return true;
      const next = items.slice(i + 1);
      const upto = next.findIndex((x) => x.section);
      const block = upto === -1 ? next : next.slice(0, upto);
      return block.length > 0;
    });

    return (
      <div className="console-side">
        <div className="console-side__brand">
          <img src={logo} alt="" />
          <div>
            <div className="name">Lubumbashi</div>
            <div className="sub">Operations console</div>
          </div>
        </div>

        <div
          className={`console-side__branch${switchable ? " is-switchable" : ""}`}
          onClick={() => switchable && this.setState({ pickerOpen: !pickerOpen })}
          title={switchable ? "Switch branch" : "You manage this branch only"}
        >
          <span className="ms">apartment</span>
          <div style={{ flex: 1, overflow: "hidden" }}>
            <div className="label">BRANCH</div>
            <div className="value">{this.currentBranchLabel()}</div>
          </div>
          {switchable && <span className="ms" style={{ color: "rgba(255,255,255,.55)" }}>unfold_more</span>}
        </div>

        {switchable && pickerOpen && (
          <div style={{ margin: "-12px 16px 16px", background: "rgba(255,255,255,.06)", borderRadius: 12, overflow: "hidden" }}>
            <div
              onClick={() => this.pick(null)}
              style={{ padding: "9px 14px", fontSize: 13, color: "#fff", cursor: "pointer" }}
            >
              All branches
            </div>
            {branches.map((b) => (
              <div
                key={b._id}
                onClick={() => this.pick(b._id)}
                style={{ padding: "9px 14px", fontSize: 13, color: "rgba(255,255,255,.85)", cursor: "pointer" }}
              >
                {b.name}
              </div>
            ))}
          </div>
        )}

        <div className="console-side__nav">
          {pruned.map((item, i) =>
            item.section ? (
              <div className="console-side__section" key={`s-${i}`}>
                {item.section.toUpperCase()}
              </div>
            ) : (
              <Link
                key={item.to}
                to={item.to}
                className={`console-side__item${path.startsWith(item.to) ? " is-active" : ""}`}
              >
                <span className="ms">{item.icon}</span>
                {item.label}
              </Link>
            )
          )}
        </div>

        <div className="console-side__user">
          <div className="avatar">{initials || "?"}</div>
          <div style={{ flex: 1, overflow: "hidden" }}>
            <div className="name">
              {[user.firstName, user.lastName].filter(Boolean).join(" ") || "Signed in"}
            </div>
            <div className="role">
              {user.isSuperAdmin
                ? "Super admin"
                : user.accessLevel === "view"
                ? "Branch staff"
                : "Branch manager"}
            </div>
          </div>
          <Link to="/logout" title="Sign out">
            <span className="ms">logout</span>
          </Link>
        </div>
      </div>
    );
  }
}

export default withRouter(ConsoleSidebar);
